/* eslint-disable i18next/no-literal-string -- later */
import { useRegisterUserMutation } from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { CountryPicker } from '@/components/base/form/country-picker';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import useAffiliateCode from '@/hooks/use-affiliate-code';
import { filteredCountriesObject, isBannedCountry } from '@/utils/country-list';
import { faker } from '@faker-js/faker';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { type FormEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const userSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email(),
  password: z.string().min(6, { message: 'Password is required' }),
  firstName: z.string().min(1, { message: 'First Name is required' }),
  lastName: z.string().min(1, { message: 'Last Name is required' }),
  countryCode: z.string().min(1, { message: 'Country is required' }),
  affiliateCode: z.string().optional(),
});

type User = z.infer<typeof userSchema>;

export function AddSubaffiliate(): JSX.Element {
  const { affiliateCode } = useAffiliateCode();
  const [register, { loading }] = useRegisterUserMutation();
  const { t } = useTranslation();

  const methods = useForm<User>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      countryCode: Object.values(filteredCountriesObject)[
        Math.floor(
          Math.random() * Object.values(filteredCountriesObject).length,
        )
      ].value,
      affiliateCode: affiliateCode ?? '',
      firstName: faker.person.firstName(),
      lastName: faker.person.lastName(),
      email: faker.internet.email(),
      password: 'monevis123',
    },
  });
  const { handleSubmit, watch } = methods;
  const watchCountry = watch('countryCode');

  async function onSubmit(values: User): Promise<void> {
    try {
      const { data } = await register({
        variables: {
          userInput: {
            email: values.email,
            password: values.password,
            firstName: values.firstName,
            lastName: values.lastName,
            countryCode: values.countryCode,
            referredBy: values.affiliateCode,
          },
          sendEmail: false,
        },
      });
      enqueueSnackbar({
        message: `Subaffiliate ${data?.register.firstName} ${data?.register.lastName} with email ${data?.register.email} added successfully. Code: ${data?.register.refcode}`,
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message:
            e.message === 'USER_ALREADY_EXISTS'
              ? t('registration.userAlreadyExists')
              : t('login.invalidCredentials'),
          variant: 'error',
        });
      }
      // console.error(error);
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      handleSubmit(onSubmit)();
    })();
  }

  return (
    <Section variant="secondary">
      <div className="flex w-full flex-col">
        <PageHeading subheading="Adding a subaffiliate is like registering a new member to the affiliate app with the current user's affiliate code">
          Add Subaffiliate
        </PageHeading>
        <PageHeading subheading="These data are random and can be modified" />

        <FormProvider {...methods}>
          <form onSubmit={handleClickSubmit} data-testid="register-form">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-5 sm:flex-row">
                  <fieldset className="w-full">
                    <FormField
                      label={t('registration.firstName')}
                      name="firstName"
                    >
                      <Input
                        variant="secondary"
                        data-testid="first-name-input"
                        placeholder={t('registration.firstNamePlaceholder')}
                      />
                    </FormField>
                  </fieldset>
                  <fieldset className="w-full">
                    <FormField
                      label={t('registration.lastName')}
                      name="lastName"
                    >
                      <Input
                        variant="secondary"
                        data-testid="last-name-input"
                        placeholder={t('registration.lastNamePlaceholder')}
                      />
                    </FormField>
                  </fieldset>
                </div>
                <div className="flex flex-col gap-5 sm:flex-row">
                  <fieldset className="w-full">
                    <FormField label={t('registration.email')} name="email">
                      <Input
                        variant="secondary"
                        data-testid="email-input"
                        placeholder={t('registration.emailPlaceholder')}
                      />
                    </FormField>
                  </fieldset>
                  <fieldset className="w-full">
                    <FormField
                      label={t('registration.password')}
                      name="password"
                    >
                      <Input
                        variant="secondary"
                        data-testid="password-input"
                        placeholder="••••••"
                      />
                    </FormField>
                  </fieldset>
                </div>

                <fieldset className="w-full">
                  <FormField
                    defaultValue={affiliateCode ?? ''}
                    label={t('registration.affiliateCode')}
                    name="affiliateCode"
                  >
                    <Input variant="secondary" data-testid="password-input" />
                  </FormField>
                </fieldset>
                <CountryPicker
                  data-testid="country-code-input"
                  name="countryCode"
                  defaultValue={watchCountry}
                />
              </div>
              <Button
                as="button"
                className="text-base font-bold"
                loading={loading}
                type="submit"
                data-testid="registration-submit-button"
                disabled={isBannedCountry(watchCountry)}
              >
                Add Subaffiliate
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Section>
  );
}
